// js/lib/firestore.js
// Firestore data access layer — shop-centric architecture.
//
// Root collections:
//   users/{uid}                          → global auth profile (name, email, role)
//   shops/{shopId}                       → shop document (name, logoUrl, ownerId)
//   shops/{shopId}/owners/{uid}          → enables isOwnerOf() rule checks
//   shops/{shopId}/services/{id}         → point-earning activities (owner-defined)
//   shops/{shopId}/rewards/{id}          → per-shop redemption rewards
//   shops/{shopId}/memberships/{userId}  → customer points balance per shop
//   shops/{shopId}/transactions/{id}     → immutable earn/redeem log
//
// Never import `db` directly from page files.

import { getStoredUtmParams } from "./utmTracker.js";

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getCountFromServer,
  addDoc,
  increment,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  documentId,
  deleteField,
  startAfter,
} from "firebase/firestore";

import { billingFunctions, db, httpsCallable } from "./firebase.js";

const ensureShopMembershipCallable = httpsCallable(
  billingFunctions,
  "ensure_shop_membership",
);

const addPointsToMembershipCallable = httpsCallable(
  billingFunctions,
  "add_points_to_membership",
);

// ─── Users ────────────────────────────────────────────────────────────────────

export async function getUser(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createUser(uid, data) {
  const utmParams = getStoredUtmParams();
  return setDoc(doc(db, "users", uid), {
    ...data,
    ...(utmParams?.utm_source ? { utm_source: utmParams.utm_source } : {}),
    createdAt: serverTimestamp(),
  });
}

export async function updateUser(uid, data) {
  return updateDoc(doc(db, "users", uid), data);
}

export async function getCustomerPayments(userId, limitCount = 50) {
  if (!userId) return [];

  const q = query(
    collection(db, "customers", userId, "payments"),
    orderBy("created", "desc"),
    limit(limitCount),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Used after Google sign-in: create a profile only if one doesn't exist yet.
 * Returns the existing or newly created profile.
 */
/**
 * Used after Google sign-in: create a profile only if one doesn't exist yet.
 * Pass `role` only during first registration — existing profiles keep their role.
 * @param {import("firebase/auth").User} firebaseUser
 * @param {"customer" | "owner"} [role="customer"]
 */
export async function getOrCreateUser(firebaseUser, role = "customer") {
  const existing = await getUser(firebaseUser.uid);

  if (existing) {
    // If the stored name is empty but we now have a displayName, patch it
    if (!existing.name && firebaseUser.displayName) {
      const patch = { name: firebaseUser.displayName };
      await updateDoc(doc(db, "users", firebaseUser.uid), patch);
      return { ...existing, ...patch };
    }
    return existing;
  }

  const utmParams = getStoredUtmParams();
  const data = {
    name: firebaseUser.displayName || "",
    email: firebaseUser.email || "",
    photoURL: firebaseUser.photoURL || "",
    role,
    // Owners must complete setup before accessing the app.
    // setupComplete is flipped to true when the setup wizard is finished.
    // The cleanupAbandonedOwners Cloud Function removes accounts that never
    // finish setup after ABANDONED_OWNER_TIMEOUT_MS.
    ...(role === "owner" ? { setupComplete: false } : {}),
    ...(utmParams?.utm_source ? { utm_source: utmParams.utm_source } : {}),
    createdAt: serverTimestamp(),
  };
  await setDoc(doc(db, "users", firebaseUser.uid), data);
  return { id: firebaseUser.uid, ...data };
}

// ─── Shops ────────────────────────────────────────────────────────────────────

export async function getShop(shopId) {
  const snap = await getDoc(doc(db, "shops", shopId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateShop(shopId, data) {
  return updateDoc(doc(db, "shops", shopId), data);
}

/**
 * Live listener for a shop document.
 * Fires immediately from IndexedDB cache, then on every server change.
 * @param {string} shopId
 * @param {(shop: object) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onShopSnapshot(shopId, callback) {
  return onSnapshot(doc(db, "shops", shopId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

export async function getShopByOwner(ownerId) {
  const q = query(collection(db, "shops"), where("ownerId", "==", ownerId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Fetch a shop document by its public slug (e.g. "psalidi").
 * Used by the customer landing page (/s/:slug) to load shop branding.
 * @param {string} slug  lowercase URL-safe identifier set by the owner
 * @returns {object|null}
 */
export async function getShopBySlug(slug) {
  if (!slug) return null;
  const q = query(collection(db, "shops"), where("slug", "==", slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Check whether a slug is available (not taken by another shop).
 * @param {string} slug          The slug to check
 * @param {string} currentShopId Exclude this shop's own doc from the uniqueness check
 * @returns {boolean} true if the slug is free to use
 */
export async function checkSlugAvailable(slug, currentShopId) {
  if (!slug) return false;
  const q = query(collection(db, "shops"), where("slug", "==", slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return true;
  // Only taken if it belongs to a *different* shop
  return snap.docs[0].id === currentShopId;
}

/**
 * Creates a shop document and registers the owner in the owners sub-collection.
 * The owners sub-collection is used by Firestore rules for isOwnerOf() checks.
 * @param {string} ownerId  Firebase UID of the owner
 * @param {object} data     Shop data
 * @param {object} [ownerMeta]  Optional owner profile snapshot { name, email, photoURL }
 */
export async function createShop(ownerId, data, ownerMeta = {}) {
  const ref = await addDoc(collection(db, "shops"), {
    ...data,
    ownerId,
    createdAt: serverTimestamp(),
  });
  await addOwnerToShop(ref.id, ownerId, ownerMeta);
  return ref;
}

/**
 * Registers a user in shops/{shopId}/owners/{uid}.
 * Called after shop creation — required for isOwnerOf() Firestore rule checks.
 * Stores owner profile info (name, email, photoURL) alongside the ownerId so
 * the sub-collection document is useful for admin queries and analytics.
 * @param {string} shopId
 * @param {string} uid
 * @param {object} [ownerMeta]  Optional { name, email, photoURL }
 */
export async function addOwnerToShop(shopId, uid, ownerMeta = {}) {
  return setDoc(doc(db, "shops", shopId, "owners", uid), {
    ownerId: uid,
    name: ownerMeta.name || "",
    email: ownerMeta.email || "",
    photoURL: ownerMeta.photoURL || "",
    createdAt: serverTimestamp(),
  });
}

// ─── Rewards — shops/{shopId}/rewards ─────────────────────────────────────────

export async function createReward(shopId, data) {
  return addDoc(collection(db, "shops", shopId, "rewards"), {
    isActive: true,
    ...data,
    shopId,
    createdAt: serverTimestamp(),
  });
}

export async function getShopRewards(shopId) {
  if (!shopId) return [];

  const rewardsRef = collection(db, "shops", shopId, "rewards");

  try {
    const q = query(rewardsRef, orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn(
      "[firestore] getShopRewards fallback to unordered query:",
      err,
    );
    const snap = await getDocs(rewardsRef);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
}

export async function getReward(shopId, rewardId) {
  const snap = await getDoc(doc(db, "shops", shopId, "rewards", rewardId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateReward(shopId, rewardId, data) {
  // Always clean up the legacy `name` field when a `title` is being written,
  // so re-opening the edit form never shows a stale old value.
  const payload = { ...data };
  if ("title" in payload) payload.name = deleteField();
  return updateDoc(doc(db, "shops", shopId, "rewards", rewardId), payload);
}

export async function deleteReward(shopId, rewardId) {
  return deleteDoc(doc(db, "shops", shopId, "rewards", rewardId));
}

/**
 * Live listener for a shop's rewards collection.
 * Fires immediately from IndexedDB cache, then on every server change.
 * @param {string} shopId
 * @param {(rewards: object[]) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onShopRewardsSnapshot(shopId, callback) {
  const rewardsRef = collection(db, "shops", shopId, "rewards");
  const q = query(rewardsRef, orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ─── Services — shops/{shopId}/services ───────────────────────────────────────

export async function getShopServices(shopId) {
  const q = query(
    collection(db, "shops", shopId, "services"),
    orderBy("createdAt", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getService(shopId, serviceId) {
  const snap = await getDoc(doc(db, "shops", shopId, "services", serviceId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createService(shopId, data) {
  return addDoc(collection(db, "shops", shopId, "services"), {
    isActive: true,
    ...data,
    shopId,
    createdAt: serverTimestamp(),
  });
}

export async function updateService(shopId, serviceId, data) {
  return updateDoc(doc(db, "shops", shopId, "services", serviceId), data);
}

export async function deleteService(shopId, serviceId) {
  return deleteDoc(doc(db, "shops", shopId, "services", serviceId));
}

/**
 * Live listener for a shop's services collection.
 * Fires immediately from IndexedDB cache, then on every server change.
 * @param {string} shopId
 * @param {(services: object[]) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onShopServicesSnapshot(shopId, callback) {
  const q = query(
    collection(db, "shops", shopId, "services"),
    orderBy("createdAt", "asc"),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ─── Memberships — shops/{shopId}/memberships/{userId} ───────────────────────

/**
 * Get a customer's membership (points balance) for a specific shop.
 */
export async function getMembership(shopId, userId) {
  const snap = await getDoc(doc(db, "shops", shopId, "memberships", userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getMembershipCount(shopId) {
  const snap = await getCountFromServer(
    collection(db, "shops", shopId, "memberships"),
  );
  return snap.data().count;
}

/**
 * Returns the membership if it exists, otherwise creates a zero-point one.
 * Retries the write up to 3 times with increasing delay to handle the
 * transient "Missing or insufficient permissions" error that occurs on first
 * load when Firestore's server connection hasn't yet applied the auth token.
 */
export async function getOrCreateMembership(
  shopId,
  userId,
  userName = "",
  userEmail = "",
  userPhoto = "",
  { selfEnroll = false } = {},
) {
  const ref = doc(db, "shops", shopId, "memberships", userId);
  console.log("[firestore] getOrCreateMembership: reading", ref.path);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const existing = { id: snap.id, ...snap.data() };
    // Backfill name/email/photo if they were previously stored as empty strings
    if (
      (!existing.userName && userName) ||
      (!existing.userEmail && userEmail) ||
      (!existing.userPhoto && userPhoto)
    ) {
      const updates = {};
      if (!existing.userName && userName) updates.userName = userName;
      if (!existing.userEmail && userEmail) updates.userEmail = userEmail;
      if (!existing.userPhoto && userPhoto) updates.userPhoto = userPhoto;
      console.log(
        "[firestore] getOrCreateMembership: backfilling",
        Object.keys(updates),
      );
      await updateDoc(ref, updates);
      return { ...existing, ...updates };
    }
    console.log("[firestore] getOrCreateMembership: found existing membership");
    return existing;
  }

  // Block owner scan from auto-enrolling a customer who hasn't registered.
  if (!selfEnroll) {
    throw new Error("MEMBERSHIP_NOT_FOUND");
  }

  console.log("[firestore] getOrCreateMembership: creating new membership");
  await ensureShopMembershipCallable({
    shopId,
    userId,
    userName: userName || "",
    userEmail: userEmail || "",
    userPhoto: userPhoto || "",
  });

  const createdSnap = await getDoc(ref);
  if (!createdSnap.exists()) {
    throw new Error("MEMBERSHIP_CREATE_FAILED");
  }

  return { id: createdSnap.id, ...createdSnap.data() };
}

/**
 * Watch a customer's membership document for real-time changes.
 * Returns an unsubscribe function. Call it to stop listening.
 */
export function onMembershipSnapshot(shopId, userId, callback) {
  const ref = doc(db, "shops", shopId, "memberships", userId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    }
  });
}

/**
 * Get all of a customer's memberships across ALL shops.
 * Uses a collectionGroup query — works because shopId is stored in each doc.
 */
export async function getCustomerMemberships(userId) {
  const membershipsRef = collectionGroup(db, "memberships");

  const q = query(membershipsRef, where("userId", "==", userId));
  let snap = await getDocs(q);

  if (snap.empty) {
    console.warn(
      "[firestore] getCustomerMemberships fallback omitted because documentId() query fails if userId is not a valid doc path.",
    );
    // Remove the fallback query because querying collectionGroup by documentId requires a full path in Firebase Web SDK.
    // We already query by `userId` above.
  }

  return snap.docs.map((d) => {
    const data = d.data();
    // Prefer the path-derived shopId — it's always canonical.
    // data.shopId may be stale or absent on older documents.
    const shopId = d.ref.parent.parent?.id || data.shopId || "";
    return { id: d.id, ...data, shopId };
  });
}

/**
 * Get all customer memberships for a specific shop, ordered by points descending.
 * @param {string} shopId
 * @returns {Promise<Array>}
 */
export async function getShopMemberships(shopId) {
  const ref = collection(db, "shops", shopId, "memberships");
  const q = query(ref, orderBy("points", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Live listener for all customer memberships in a shop.
 * Fires immediately on attach, then on every server change.
 * Results are sorted client-side by points descending so that documents
 * without a `points` field (legacy or brand-new) are still included.
 * @param {string} shopId
 * @param {(members: object[]) => void} callback
 * @param {(err: Error) => void} [onError]
 * @returns {() => void} unsubscribe function
 */
export function onShopMembershipsSnapshot(shopId, callback, onError) {
  const ref = collection(db, "shops", shopId, "memberships");
  console.log(
    "[DIAG] onShopMembershipsSnapshot: attaching listener | shopId=",
    shopId,
  );
  return onSnapshot(
    ref,
    (snap) => {
      console.log(
        "[DIAG] onShopMembershipsSnapshot: snapshot received",
        "| docs:",
        snap.size,
        "| fromCache:",
        snap.metadata.fromCache,
        "| hasPendingWrites:",
        snap.metadata.hasPendingWrites,
      );
      const members = snap.docs
        .map((d) => {
          console.log(
            "[DIAG]   membership doc | id=",
            d.id,
            "| points=",
            d.data().points ?? "MISSING",
          );
          return { id: d.id, ...d.data() };
        })
        .sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
      console.log(
        "[DIAG] onShopMembershipsSnapshot: delivering",
        members.length,
        "members to callback",
      );
      callback(members);
    },
    (err) => {
      console.error(
        "[DIAG] onShopMembershipsSnapshot ERROR | code:",
        err?.code,
        "| message:",
        err?.message,
        err,
      );
      onError?.(err);
    },
  );
}

/**
 * Persist the customer's hidden reward IDs in their membership document.
 * @param {string} shopId
 * @param {string} userId
 * @param {string[]} hiddenRewards — array of reward IDs the customer has opted out of
 */
export async function updateRewardPreferences(shopId, userId, hiddenRewards) {
  const ref = doc(db, "shops", shopId, "memberships", userId);
  await updateDoc(ref, {
    hiddenRewards: Array.isArray(hiddenRewards) ? hiddenRewards : [],
  });
}

/**
 * Save optional customer contact info (phone, birthday) to their membership.
 * Both fields are optional — pass only what changed.
 * birthday: { day: number, month: number } | null
 */
export async function updateMembershipProfile(
  shopId,
  userId,
  { phone, birthday } = {},
) {
  const ref = doc(db, "shops", shopId, "memberships", userId);
  const updates = { updatedAt: serverTimestamp() };
  if (phone !== undefined) updates.phone = phone;
  if (birthday !== undefined) updates.birthday = birthday;
  await updateDoc(ref, updates);
}

/**
 * Add points to a customer's membership and record a positive transaction.
 * Delegates to the add_points_to_membership Cloud Function so that the free
 * plan member cap is enforced server-side and cannot be bypassed by calling
 * the Firestore SDK directly from the client.
 *
 * `services` may be a single { id, name, points } object (legacy single
 * service flow) or an array of such objects when the owner selects more
 * than one service in the same visit — the cloud function sums their points
 * into one transaction.
 */
export async function addPointsToMembership(
  shopId,
  userId,
  userName,
  userEmail,
  services,
) {
  const serviceList = Array.isArray(services) ? services : [services];
  const result = await addPointsToMembershipCallable({
    shopId,
    userId,
    userName: userName || "",
    userEmail: userEmail || "",
    services: serviceList.map((s) => ({
      serviceId: s.id || null,
      serviceName: s.name || "",
      points: s.points,
    })),
  });

  if (!result.data?.success) {
    throw new Error("ADD_POINTS_FAILED");
  }
}

/**
 * Redeem a reward: deduct points from the membership and record a redeem
 * transaction. Throws if the customer does not have enough points.
 * Must be called from the customer client (self-redemption).
 */
export async function redeemReward(
  shopId,
  userId,
  userName,
  userEmail,
  rewardId,
  rewardTitle,
  pointsRequired,
) {
  const memberRef = doc(db, "shops", shopId, "memberships", userId);
  const txRef = doc(collection(db, "shops", shopId, "transactions"));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(memberRef);

    if (!snap.exists()) {
      throw new Error("MEMBERSHIP_NOT_FOUND");
    }

    const currentPoints = snap.data().points ?? 0;

    if (currentPoints < pointsRequired) {
      throw new Error("NOT_ENOUGH_POINTS");
    }

    tx.update(memberRef, {
      points: increment(-pointsRequired),
      updatedAt: serverTimestamp(),
    });

    tx.set(txRef, {
      userId,
      userName: userName || "",
      userEmail: userEmail || "",
      shopId,
      type: "redeem",
      points: -pointsRequired,
      rewardId: rewardId || null,
      description: rewardTitle || "Εξαργύρωση",
      createdAt: serverTimestamp(),
    });
  });
}

// ─── Push subscriptions — shops/{shopId}/memberships/{userId}.pushSubscriptions
// Subscriptions are stored per-membership (per-shop) so each shop's push
// notifications are isolated.  The map is keyed by a hash of the endpoint URL.
// This allows one user to be subscribed from multiple devices/browsers.

/**
 * Persist a Web Push subscription for a specific device/browser.
 * Uses a nested field path so other devices' entries are never overwritten.
 *
 * @param {string} shopId  Shop ID (parent of the membership doc)
 * @param {string} uid     Firebase Auth UID (membership doc ID)
 * @param {string} key     Stable 32-char hex key derived from subscription.endpoint
 * @param {object} sub     PushSubscription JSON (endpoint + keys)
 */
export async function savePushSubscription(shopId, uid, key, sub) {
  return updateDoc(doc(db, "shops", shopId, "memberships", uid), {
    [`pushSubscriptions.${key}`]: sub,
  });
}

/**
 * Replace the entire pushSubscriptions map with a single entry for this device.
 * Called during migration from the old endpoint-hash scheme to the stable
 * device-key scheme. Wipes accumulated stale entries in one write.
 *
 * @param {string} shopId  Shop ID
 * @param {string} uid     Firebase Auth UID
 * @param {string} key     Stable device key from getDeviceKey()
 * @param {object} sub     PushSubscription JSON (endpoint + keys)
 */
export async function resetPushSubscriptions(shopId, uid, key, sub) {
  return updateDoc(doc(db, "shops", shopId, "memberships", uid), {
    pushSubscriptions: { [key]: sub },
  });
}

// ─── Transactions — shops/{shopId}/transactions ───────────────────────────────

/**
 * Get all transactions for a specific customer in a shop, newest first.
 */
export async function getCustomerTransactionsInShop(shopId, userId) {
  const q = query(
    collection(db, "shops", shopId, "transactions"),
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a customer's transactions across ALL shops.
 */
export async function getCustomerTransactions(userId) {
  const q = query(
    collectionGroup(db, "transactions"),
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get the most recent N transactions for a shop (all customers), newest first.
 * Used in the owner home dashboard recent-activity feed.
 */
export async function getRecentShopTransactions(shopId, limitCount = 8) {
  const q = query(
    collection(db, "shops", shopId, "transactions"),
    orderBy("createdAt", "desc"),
    limit(limitCount),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a page of shop transactions (all customers), newest first, with cursor-based pagination.
 * Pass `lastDoc` (the Firestore DocumentSnapshot of the last item from the previous page) to
 * fetch the next page. When `lastDoc` is null/undefined the first page is returned.
 * Returns { transactions, lastDoc } where `lastDoc` is the snapshot of the last fetched doc
 * (pass it on the next call) or null when there are no more results.
 */
export async function getShopTransactionsPaginated(
  shopId,
  lastDoc = null,
  limitCount = 30,
) {
  const constraints = [orderBy("createdAt", "desc"), limit(limitCount)];
  if (lastDoc) constraints.push(startAfter(lastDoc));
  const q = query(
    collection(db, "shops", shopId, "transactions"),
    ...constraints,
  );
  const snap = await getDocs(q);
  const transactions = snap.docs.map((d) => ({
    id: d.id,
    _snap: d,
    ...d.data(),
  }));
  const newLastDoc =
    snap.docs.length === limitCount ? snap.docs[snap.docs.length - 1] : null;
  return { transactions, lastDoc: newLastDoc };
}

/**
 * Get all shop transactions (all customers) created on or after `since` (a JS Date).
 * Used for today's KPI counts on the owner home dashboard.
 */
export async function getShopTransactionsSince(shopId, since) {
  const q = query(
    collection(db, "shops", shopId, "transactions"),
    where("createdAt", ">=", since),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get all shop transactions between two JS Dates (inclusive start, exclusive end).
 * Used by the analytics page for flexible time-range queries.
 */
export async function getTransactionsBetween(shopId, from, to) {
  const q = query(
    collection(db, "shops", shopId, "transactions"),
    where("createdAt", ">=", from),
    where("createdAt", "<", to),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get the total number of unique customers (memberships) for a shop.
 * Uses Firestore's aggregation count — O(1) read cost regardless of size.
 */
export async function getShopCustomerCount(shopId) {
  const q = query(collection(db, "shops", shopId, "memberships"));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/**
 * Get the number of new customers (memberships) created since a specific date.
 */
export async function getShopNewCustomerCountSince(shopId, since) {
  const q = query(
    collection(db, "shops", shopId, "memberships"),
    where("createdAt", ">=", since),
  );
  const snap = await getDocs(q);
  return snap.size;
}

// ─── Broadcasts — shops/{shopId}/broadcasts ───────────────────────────────────

/**
 * Create a new broadcast message for a shop.
 * The Cloud Function picks it up, fans out push notifications,
 * then updates status → "sent" and fills sentAt / recipientCount.
 *
 * @param {string} shopId
 * @param {{ title: string, body: string, recipientUserIds?: string[] | null }} data
 * @returns {Promise<string>} the new broadcast document ID
 */
export async function createBroadcast(
  shopId,
  { title, body, recipientUserIds = null },
) {
  const docData = {
    title,
    body,
    status: "pending",
    sentAt: null,
    recipientCount: 0,
    createdAt: serverTimestamp(),
  };
  // Only write the field when targeting specific users — omitting it means "all".
  if (Array.isArray(recipientUserIds) && recipientUserIds.length > 0) {
    docData.recipientUserIds = recipientUserIds;
  }
  const ref = await addDoc(
    collection(db, "shops", shopId, "broadcasts"),
    docData,
  );
  return ref.id;
}

/**
 * Real-time listener for all broadcasts of a shop, newest first.
 *
 * @param {string} shopId
 * @param {(broadcasts: Array) => void} callback
 * @returns {() => void} unsubscribe function
 */
export function onBroadcastsSnapshot(shopId, callback) {
  const q = query(
    collection(db, "shops", shopId, "broadcasts"),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ─── Customer notification inbox ──────────────────────────────────────────────
// Persisted owner broadcast messages stored under users/{uid}/notifications.
// Written by the onBroadcast Cloud Function; readable/deletable by the customer.

/**
 * Fetch all inbox notifications for a customer, newest first.
 * @param {string} uid
 * @returns {Promise<Array>}
 */
export async function getCustomerNotifications(uid) {
  const q = query(
    collection(db, "users", uid, "notifications"),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Real-time listener for a customer's notifications, newest first.
 * Always scoped to a specific shop so a multi-shop user only sees messages
 * from the shop they are currently visiting.
 *
 * @param {string} uid
 * @param {string} shopId  Only notifications with this shopId are returned.
 * @param {(notifications: Array) => void} callback
 * @returns {() => void} unsubscribe
 */
export function onNotificationsSnapshot(uid, shopId, callback) {
  const q = query(
    collection(db, "users", uid, "notifications"),
    where("shopId", "==", shopId),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.error("[onNotificationsSnapshot]", err);
    },
  );
}

/**
 * Real-time unread count listener, scoped to a specific shop.
 *
 * @param {string} uid
 * @param {string} shopId  Only notifications with this shopId are counted.
 * @param {(count: number) => void} callback
 * @returns {() => void} unsubscribe
 */
export function onUnreadNotificationsCount(uid, shopId, callback) {
  const q = query(
    collection(db, "users", uid, "notifications"),
    where("shopId", "==", shopId),
    where("read", "==", false),
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.size),
    (err) => {
      console.error("[onUnreadNotificationsCount]", err);
    },
  );
}

/**
 * Mark a notification as read.
 * @param {string} uid
 * @param {string} notifId
 */
export async function markNotificationRead(uid, notifId) {
  await updateDoc(doc(db, "users", uid, "notifications", notifId), {
    read: true,
  });
}

/**
 * Delete a notification.
 * @param {string} uid
 * @param {string} notifId
 */
export async function deleteNotification(uid, notifId) {
  await deleteDoc(doc(db, "users", uid, "notifications", notifId));
}
